import { useState, useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { useAppContext } from 'services/context';
import { COURSES_ACTIONS } from 'services/reducers/actionTypes';
import { useTranslation } from 'services/hooks';
import { COURSE_IDS, COURSE_TYPES, LANGUAGES, ROUTES } from 'constants/index';

import AudioPlayer from 'components/AudioPlayer';
import Button from 'components/Button';
import Container from 'components/Container';
import Icon from 'components/Icon';
import Question from 'pages/Quiz/Question';
import Pagination from 'components/Pagination';
import Modal from 'components/Modal';
import Timer from 'components/Timer';

import './style.css';

export const COOL_DOWN_TIME_IN_HOURS = 48;

const TRANSLATION_KEY = 'final_exam';

const IELTSFinalExam = () => {
	const { Api, courses, dispatchCourses } = useAppContext();
	const { chapterId, recordId } = useParams();

	const navigate = useNavigate();

	const getTranslation = useTranslation();

	const [test, setTest] = useState();
	const [userAnswers, setUserAnswers] = useState({});
	const [passedTest, setPassedTest] = useState(false);
	const [isFinished, setIsFinished] = useState(false);
	const [questionOrderNumber, setQuestionOrderNumber] = useState(1);
	const [currentSectionNumber, setCurrentSectionNumber] = useState(1);
	const [showTabs, setShowTabs] = useState(true);
	const [timerMinutesAndSeconds, setTimerMinutesAndSeconds] = useState({});
	const [blockByCooldown, setBlockByCooldown] = useState(false);
	const [currentCourse, setCurrentCourse] = useState();

	// useEffect(() => {
	// 	const _currentCourse = courses && courses?.courses?.find(course => course.recordId === parseInt(recordId));
	// 	if (!_currentCourse) return;

	// 	setCurrentCourse(_currentCourse);
	// 	switch (_currentCourse.courseTypeId) {
	// 		case COURSE_TYPES.TEF: //test time -> 120 minutes
	// 			if (_currentCourse.courseId === COURSE_IDS.TCF) {
	// 				setTimerMinutesAndSeconds({ seconds: 59, minutes: 95 });
	// 			} else {
	// 				setTimerMinutesAndSeconds({ seconds: 59, minutes: 119 });
	// 			}
	// 			break;
	// 		case COURSE_TYPES.IELTS:
	// 		case COURSE_TYPES.IELTS_GENERAL: //test time -> IELTS,IELTSGENERAL 60 minutes
	// 			setTimerMinutesAndSeconds({ seconds: 59, minutes: 59 });
	// 			break;
	// 		case COURSE_TYPES.TOFEL: //test time -> TOEFL 90 minutes,
	// 			setTimerMinutesAndSeconds({ seconds: 59, minutes: 89 });
	// 			break;
	// 		default: //anything else 60 minutes
	// 			setTimerMinutesAndSeconds({ seconds: 59, minutes: 59 });
	// 			break;
	// 	}
	// }, [courses, recordId]);

	//returns all test questions by sectionId
	const currentSection = useMemo(() => {
		try {
			if (!test || !currentSectionNumber) return [];
			const _currentTestSection = test?.courseTestSections?.find(({ sectionOrder }) => sectionOrder === currentSectionNumber);

			return _currentTestSection || {};
		} catch (ex) {}
	}, [currentSectionNumber, test]);

	const onLastSection = useMemo(
		() => !test?.courseTestSections?.some(({ sectionOrder }) => sectionOrder > currentSectionNumber) || false,
		[test, currentSectionNumber]
	);

	//check if we can move to previous question
	const allowMoveToPrevChapter = useMemo(() => {
		//dont allow only for first chapter at the first question
		if (currentSectionNumber === 1 && questionOrderNumber === 1) return false;
		return true;
	}, [questionOrderNumber, currentSectionNumber]);

	//check if we are at the last question on the last section
	const disabledNextChpater = useMemo(() => {
		if (!onLastSection || !currentSection?.courseTestQuestions) return false;
		const questionNumbers = currentSection.courseTestQuestions.map(({ questionNumberInTest }) => questionNumberInTest);
		const currentSectionLastQuestionNumber = Math.max(...questionNumbers);

		return questionOrderNumber === currentSectionLastQuestionNumber;
	}, [onLastSection, currentSection, questionOrderNumber]);

	//returns all test questions by sectionId
	const totalQuestionsLength = useMemo(() => {
		try {
			if (!test) return 0;
			const _totalQuestionsLength = test.courseTestSections.reduce((acc, section) => acc + section.courseTestQuestions.length, 0);
			return _totalQuestionsLength;
		} catch (ex) {
			return 0;
		}
	}, [test]);

	//returns current question by question order number
	const currentQuestion = useMemo(() => {
		if (!currentSection?.courseTestQuestions) return {};
		const question = currentSection.courseTestQuestions.find(question => question.questionNumberInTest === questionOrderNumber);
		return { ...question };
	}, [currentSection, questionOrderNumber]);

	//handle tabs changing
	const _handleTabsChange = sectionOrder => {
		if (!currentSectionNumber || !sectionOrder) return;
		if (currentSectionNumber !== sectionOrder) {
			setCurrentSectionNumber(sectionOrder);
		}
	};

	const validateQuiz = (testToValidate, answersToValidate) => {
		const validatedTest = { ...testToValidate };
		let passed = true;

		const rightAnswers = testToValidate.courseTestSections.reduce((acc, section) => {
			const _rightAnswers = section?.courseTestQuestions?.map(({ courseTestQuestionAnswers }) =>
				courseTestQuestionAnswers.find(({ isCorrectAnswer }) => isCorrectAnswer)
			);
			return [...acc, ..._rightAnswers];
		}, []);

		if (!rightAnswers) return {};

		validatedTest.courseTestSections = validatedTest.courseTestSections.map(section => {
			const _section = { ...section };
			_section.courseTestQuestions = section.courseTestQuestions.map(question => {
				const currentRightAnswer = rightAnswers.find(answer => answer.questionId === question.questionId);
				const currQuestionId = answersToValidate[currentRightAnswer.questionId];
				let isValid = currQuestionId?.answerId === currentRightAnswer?.answerId;
				if (isValid) question.answerStatus = 'correct';
				else {
					question.answerStatus = 'incorrect';
					passed = false;
				}
				return question;
			});
			return _section;
		});
		return { validatedTest, passed };
	};

	//work when user will choose answer on the test
	const _handleUserAnswer = ({ answerId, questionId }) => {
		setPassedTest(false);
		const tempTest = { ...test };
		const question = currentSection.courseTestQuestions.find(ques => ques.questionId === questionId);
		delete question.answerStatus;

		question.courseTestQuestionAnswers = question.courseTestQuestionAnswers.map(answer => {
			if (answer.answerId === answerId) answer.checked = true;
			else answer.checked = false;
			return answer;
		});
		const newAnswers = { ...userAnswers };
		newAnswers[questionId] = { answerId, sectionId: question.sectionId };
		setTest(tempTest);
		setUserAnswers(newAnswers);
	};

	//the init of test - give us test data and validate if user is allowed
	useEffect(() => {
		try {
			(async () => {
				const updateTestStartTime = async () => {
					const { studentTestStartTime } = await Api.UpdateStudentTestStartTime(recordId);
					startTimeFromCache = studentTestStartTime;
					startTimeFromCache = moment(startTimeFromCache, 'DD-MM-YYYY hh:mm:ss');
				};

				setCurrentCourse(courses.currentCourse);
				const { passedIELTSTest } = courses.currentCourse;

				if (passedIELTSTest) {
					setIsFinished(true);
					setPassedTest(true);
					return;
				}

				const courseTest = await Api.GetTestForCourseChapter(courses?.currentCourse?.courseId, chapterId);
				if (!courseTest) {
					setBlockByCooldown(true);
					console.warn('Error in GetTestForCourseChapter - no value for test');
					return;
				}
				const nowDateAndTimeString = moment(courseTest?.timeFromDB).format('DD-MM-YYYY hh:mm:ss'); //GMT+5 returns Atlanta time (GMT-5)
				const nowDateAndTime = moment(nowDateAndTimeString, 'DD-MM-YYYY hh:mm:ss');

				let startTimeFromCache = await Api.GetStudentTestStartTime(recordId);
				if (startTimeFromCache) startTimeFromCache = moment(startTimeFromCache, 'DD-MM-YYYY hh:mm:ss');

				let doNeedToUpdateStartTime = !moment.isMoment(startTimeFromCache);
				if (doNeedToUpdateStartTime) {
					await updateTestStartTime();
				}
				let durationInHours;
				if (courseTest.courseTypeId === COURSE_TYPES.TEF) {
					if (courseTest.courseId === COURSE_IDS.TCF) durationInHours = 1.6;
					else durationInHours = 2;
				} else if (courseTest.courseTypeId === COURSE_TYPES.TOFEL) durationInHours = 1.5;
				else durationInHours = 1;
				//The difference is calculated in seconds to support half hours situations.
				const startAndEndDifference = Math.abs(nowDateAndTime.diff(startTimeFromCache, 'seconds') / 60 / 60);
				//startAndEndDifference spouse to be 0 -it means he taken test right now or greater then 48 it means he finished cooldown
				let onCoolDownTime = startAndEndDifference >= durationInHours && startAndEndDifference <= 48;

				if (!passedIELTSTest && onCoolDownTime) {
					setBlockByCooldown(true);
					return;
				}

				if (!nowDateAndTime?.isSame(startTimeFromCache, 'day')) await updateTestStartTime();

				//CALC TIMER TIME
				const endTime = startTimeFromCache.clone().add(durationInHours, 'hours');
				const duration = moment.duration(Math.abs(nowDateAndTime.diff(endTime)));
				const hours = duration.hours();
				let minutes = duration.minutes();
				const seconds = duration.seconds();
				if (hours) minutes = minutes + hours * 60;
				setTimerMinutesAndSeconds({ minutes, seconds });

				setTest(courseTest);

				// // //check for old answers
				// const prevAnswers = await Api.GetStudentAnswers(courseTest.testId, recordId);
				// if (!prevAnswers || !passedIELTSTest) {
				// 	setTest(courseTest);
				// 	return;
				// }

				// //update user answers cache
				// const newAnswers = prevAnswers.reduce((prevAns, curr) => {
				// 	prevAns[curr.questionId] = curr.answerId;
				// 	return prevAns;
				// }, {});

				// const { validatedTest } = validateQuiz(courseTest, newAnswers);

				// //check user previous answers - and update view
				// validatedTest.courseTestSections.forEach(section => {
				// 	section.courseTestQuestions.forEach(question => {
				// 		const userOldAnswer = prevAnswers.find(a => a.questionId === question.questionId);
				// 		question.courseTestQuestionAnswers = question.courseTestQuestionAnswers.map(answer => {
				// 			answer.checked = userOldAnswer?.answerId === answer.answerId;
				// 			return answer;
				// 		});
				// 	});
				// });
				// setTest(validatedTest);
				// setUserAnswers(newAnswers);
			})();
		} catch (error) {
			console.warn('FINAL EXAM TEST INIT ' + error);
		}
	}, []);

	//reset order number on moving between sections
	useEffect(() => {
		setQuestionOrderNumber(1);
	}, [currentSectionNumber]);

	//handle redirect of failed test - on popup
	const _handleMoveToHome = () => {
		setPassedTest(false);
		setBlockByCooldown(false);
		navigate(`/${ROUTES.PERSONAL_AREA}/`);
	};

	//handle pagination next question
	const _handleNextQuestion = newQuestionOrderNumber => {
		const goForward = newQuestionOrderNumber > questionOrderNumber;
		let sectionProgressing = 0;
		if (!goForward && newQuestionOrderNumber < 1) sectionProgressing = -1;
		//let us know if we are at the last question on section - then change section
		if (newQuestionOrderNumber > currentSection?.courseTestQuestions?.length) sectionProgressing = 1;
		if (sectionProgressing) {
			setCurrentSectionNumber(prev => prev + sectionProgressing);
			setQuestionOrderNumber(1);
			return;
		}
		setQuestionOrderNumber(newQuestionOrderNumber);
	};

	const _getTestResults = async e => {
		e?.preventDefault();
		setPassedTest(false);

		const { validatedTest } = validateQuiz(test, userAnswers);

		setTest(validatedTest);

		const updateTestReqBody = {
			recordId: parseInt(recordId),
			chapterId: parseInt(chapterId),
			isFinal: test.isFinal,
			userAnswers: Object.keys(userAnswers).map(key => ({
				questionId: key,
				answerId: userAnswers[key].answerId,
				sectionId: userAnswers[key].sectionId,
			})),
			timeFinished: false,
		};

		const serverPassed = await Api.UpdateTestStudent(updateTestReqBody);

		setBlockByCooldown(!serverPassed);

		if (!serverPassed) return;
		setPassedTest(true);
		const userCourses = await Api.GetUserCourses();
		if (userCourses?.length > 0) dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
	};

	const _handleRunningOutOfTime = async () => {
		const updateTestReqBody = {
			recordId: parseInt(recordId),
			chapterId: parseInt(chapterId),
			isFinal: test.isFinal,
			userAnswers: Object.keys(userAnswers).map(key => ({
				questionId: key,
				answerId: userAnswers[key].answerId,
				sectionId: userAnswers[key].sectionId,
			})),
			timeFinished: true,
		};
		const serverPassed = await Api.UpdateTestStudent(updateTestReqBody);
		setBlockByCooldown(!serverPassed);

		if (!serverPassed) return;
		setPassedTest(true);
		const userCourses = await Api.GetUserCourses();
		if (userCourses?.length > 0) dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
	};

	//RENDER FUNCTIONS
	const _renderQuestion = (
		{
			paragraphTitle,
			questionNumberInTest,
			questionParagraph,
			questionsText,
			questionInstruction,
			questionId,
			courseTestQuestionAnswers,
			answerStatus,
			questionAudioLink,
			questionImageLink,
		},
		idx
	) => {
		return (
			<>
				{questionInstruction && <div className="test_wrapper__explanation" dangerouslySetInnerHTML={{ __html: questionInstruction }} />}
				{paragraphTitle && <div className="test_wrapper__text_title" dangerouslySetInnerHTML={{ __html: paragraphTitle }} />}
				{questionParagraph && <div className="test_wrapper__text_paragraph" dangerouslySetInnerHTML={{ __html: questionParagraph }} />}
				{questionImageLink && (
					<div className="test_wrapper__image_container">
						<img
							src={questionImageLink}
							className="test_wrapper__image"
							style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'scale-down' }}
						/>
					</div>
				)}
				<div className="test_wrapper__text_question">
					{questionAudioLink && (
						<>
							<div className="test_wrapper__audio">
								<AudioPlayer src={questionAudioLink} />
							</div>
						</>
					)}
					<Question
						id={questionId}
						title={questionsText}
						key={idx}
						answers={courseTestQuestionAnswers}
						onAnswer={_handleUserAnswer}
						questionNumberInTest={questionNumberInTest}
						answerStatus={answerStatus}
						disabled={answerStatus === 'correct'}
					/>
				</div>
			</>
		);
	};

	const _renderTab = ({ sectionId, sectionOrder, sectionName, id }, idx) => {
		const icons = {
			Reading: 'readingTabIcon',
			Writing: 'writingTabIcon',
			Listening: 'listeningTabIcon',
			Grammar: 'grammerTabIcon',
		};

		return (
			<TestTab
				key={idx}
				sectionId={sectionId}
				title={sectionName}
				icon={icons[sectionName]}
				isFocused={currentSectionNumber === sectionOrder}
				onClick={() => _handleTabsChange(sectionOrder)}
			/>
		);
	};

	const testTabsClasses = classNames('test_tabs', { active: showTabs });

	if (blockByCooldown)
		return (
			<Container className="FinalExam">
				<Modal isVisible={blockByCooldown} hideCloseButton>
					<TestResultModal
						passed={false}
						onClick={_handleMoveToHome}
						courseType={(currentCourse && currentCourse?.courseTypeId) || undefined}
						courseId={(currentCourse && currentCourse?.courseId) || undefined}
					>
						<Confetti isShown={true} />
					</TestResultModal>
				</Modal>
			</Container>
		);

	return (
		<Container className="FinalExam">
			{test && (
				<>
					<div className="header">
						<div className="header__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_title`)}</div>

						{!isFinished && (
							<div className="header__timer">
								<Timer
									initialMinute={timerMinutesAndSeconds.minutes}
									initialSeconds={timerMinutesAndSeconds.seconds}
									onExpiring={_handleRunningOutOfTime}
								/>
							</div>
						)}
					</div>
					<div className={`toggle_section_list ${showTabs ? 'active' : ''}`} onClick={() => setShowTabs(prev => !prev)}>
						{currentSection?.sectionDisplayText || ''}
					</div>

					<div className={testTabsClasses}>{test && test?.courseTestSections?.map(_renderTab)}</div>
					<div className="test_wrapper">
						{currentQuestion && _renderQuestion(currentQuestion)}
						<Pagination
							currentChapter={questionOrderNumber || 0}
							handleUpdateChapter={_handleNextQuestion}
							totalChapters={totalQuestionsLength || 0}
							chapterOrderInCourse={questionOrderNumber}
							disabledNextChpater={disabledNextChpater}
							allowMoveToPrevChapter={allowMoveToPrevChapter}
							nextChapterTitle={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_pagination_next`)}
							prevChapterTitle={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_pagination_prev`)}
						>
							{disabledNextChpater && (
								<Button
									title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_button`)}
									small
									disabled={!disabledNextChpater}
									onClick={_getTestResults}
									extraClassName="testResultBtn"
								/>
							)}
						</Pagination>
					</div>
				</>
			)}
			<Modal isVisible={passedTest} hideCloseButton>
				<TestResultModal
					passed={passedTest}
					onClick={_handleMoveToHome}
					courseType={(currentCourse && currentCourse?.courseTypeId) || undefined}
					courseId={(currentCourse && currentCourse?.courseId) || undefined}
				>
					<Confetti isShown={true} />
				</TestResultModal>
			</Modal>
		</Container>
	);
};

export default IELTSFinalExam;

const TestTab = ({ sectionId, title, icon, isFocused, color = '#d4d4d4', onClick }) => {
	const classes = classNames('TestTab', { focus: isFocused });

	return (
		<div className={classes} id={sectionId} onClick={onClick}>
			<div className="TestTab__title">{title}</div>
			{icon && <Icon icon={icon} color={isFocused ? '#d41028' : color} size={52} />}
		</div>
	);
};

const TestResultModal = ({ children, onClick, passed, courseType, courseId }) => {
	const getTranslation = useTranslation();

	const className = classNames('TestResultModal', {
		passed: passed,
		not_passed: !passed,
	});

	const IELTSModalProps = {
		failed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_failed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_failed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_failed_button`),
		},
		completed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_completed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_completed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_IELTS_completed_button`),
		},
	};

	const TEFModalprops = {
		failed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_failed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_failed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_failed_button`),
		},
		completed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_completed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_completed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TEF_completed_button`),
		},
	};
	const TCFModalprops = {
		failed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_failed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_failed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_failed_button`),
		},
		completed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_completed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_completed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TCF_completed_button`),
		},
	};

	const TOEFLModalProps = {
		failed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_failed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_failed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_failed_button`),
		},
		completed: {
			title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_completed_title`),
			text: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_completed_text`),
			btnText: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_TOEFL_completed_button`),
		},
	};

	const courseProperties = useMemo(() => {
		if (courseType === COURSE_TYPES.TEF) {
			if (courseId === COURSE_IDS.TCF) return TCFModalprops;
			return TEFModalprops;
		} else if (courseType === COURSE_TYPES.TOFEL) return TOEFLModalProps;
		else return IELTSModalProps;
	}, [courseType]);

	return (
		<div className={className}>
			<div className="TestResultModal__title">{passed ? courseProperties.completed.title : courseProperties.failed.title}</div>
			<div className="TestResultModal__text">{passed ? courseProperties.completed.text : courseProperties.failed.text}</div>
			<Button title={passed ? courseProperties.completed.btnText : courseProperties.failed.btnText} onClick={onClick} />
			{passed && children}
		</div>
	);
};

const Confetti = ({ isShown }) => {
	const [showConfetti, setShowConfetti] = useState(false);

	useEffect(() => {
		if (isShown) {
			setShowConfetti(true);
			setTimeout(() => {
				setShowConfetti(false);
			}, 5000);
		}
	}, [isShown]);

	return !showConfetti ? null : (
		<div className="confetti">
			{isShown && (
				<>
					<i style={{ '--speed': 10, '--bg': 'yellow' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'white' }} className="pentagram"></i>
					<i style={{ '--speed': 29, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 17, '--bg': 'blue' }} className="hexagram"></i>
					<i style={{ '--speed': 33, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 26, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 24, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 20, '--bg': 'blue' }} className="wavy-line"></i>
					<i style={{ '--speed': 40, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 17, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 25, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 15, '--bg': 'yellow' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 25, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 23, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 37, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 36, '--bg': 'white' }} className="hexagram"></i>
					<i style={{ '--speed': 32, '--bg': 'green' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 29, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 23, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 30, '--bg': 'pink' }} className="rectangle"></i>
					<i style={{ '--speed': 30, '--bg': 'red' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 19, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 16, '--bg': 'blue' }} className="hexagram"></i>
					<i style={{ '--speed': 23, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 34, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 39, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 40, '--bg': 'purple' }} className="square"></i>
					<i style={{ '--speed': 21, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 14, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 38, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 19, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 29, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 21, '--bg': 'white' }} className="hexagram"></i>
					<i style={{ '--speed': 17, '--bg': 'purple' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 23, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 48, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 38, '--bg': 'pink' }} className="rectangle"></i>
					<i style={{ '--speed': 13, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 49, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 19, '--bg': 'cyan' }} className="wavy-line"></i>
					<i style={{ '--speed': 15, '--bg': 'steelblue' }} className="square"></i>
					<i style={{ '--speed': 10, '--bg': 'yellow' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'white' }} className="pentagram"></i>
					<i style={{ '--speed': 29, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 17, '--bg': 'blue' }} className="hexagram"></i>
					<i style={{ '--speed': 33, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 26, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 24, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 15, '--bg': 'white' }} className="wavy-line"></i>
					<i style={{ '--speed': 40, '--bg': 'purple' }} className="square"></i>
					<i style={{ '--speed': 17, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 25, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 15, '--bg': 'cyan' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 45, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 23, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 37, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 26, '--bg': 'white' }} className="hexagram"></i>
					<i style={{ '--speed': 32, '--bg': 'cyan' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 45, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 23, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 50, '--bg': 'pink' }} className="rectangle"></i>
					<i style={{ '--speed': 30, '--bg': 'red' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 19, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 16, '--bg': 'blue' }} className="hexagram"></i>
					<i style={{ '--speed': 23, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 33, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 39, '--bg': 'white' }} className="wavy-line"></i>
					<i style={{ '--speed': 40, '--bg': 'orange' }} className="square"></i>
					<i style={{ '--speed': 21, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 14, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 38, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 19, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 29, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 34, '--bg': 'white' }} className="hexagram"></i>
					<i style={{ '--speed': 17, '--bg': 'indigo' }} className="wavy-line"></i>
					<i style={{ '--speed': 32, '--bg': 'yellow' }} className="pentagram"></i>
					<i style={{ '--speed': 23, '--bg': 'white' }} className="square"></i>
					<i style={{ '--speed': 18, '--bg': 'green' }} className="rectangle"></i>
					<i style={{ '--speed': 37, '--bg': 'red' }} className="dodecagram"></i>
					<i style={{ '--speed': 48, '--bg': 'pink' }} className="wavy-line"></i>
					<i style={{ '--speed': 38, '--bg': 'pink' }} className="rectangle"></i>
					<i style={{ '--speed': 13, '--bg': 'red' }} className="pentagram"></i>
					<i style={{ '--speed': 49, '--bg': 'yellow' }} className="dodecagram"></i>
					<i style={{ '--speed': 19, '--bg': 'purple' }} className="wavy-line"></i>
					<i style={{ '--speed': 15, '--bg': 'cyan' }} className="square"></i>
				</>
			)}
		</div>
	);
};
